import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Field, Input } from '../../components/ui';
import { ApiError, api, type LoginChallenge } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthShell, fieldErrors } from './AuthShell';

export function Login() {
  usePageTitle('Sign in — WaveQA');

  const [challenge, setChallenge] = useState<LoginChallenge | null>(null);

  // A correct password earns a challenge, not a session (spec 14), so this
  // screen has two states rather than two routes - the challenge id must not
  // survive a page reload.
  return challenge ? (
    <OtpStep challenge={challenge} onExpired={() => setChallenge(null)} />
  ) : (
    <PasswordStep onChallenge={setChallenge} />
  );
}

function PasswordStep({ onChallenge }: { onChallenge: (c: LoginChallenge) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      onChallenge(await api.login(email, password));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('Something went wrong.', 0));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle="We will email you a one-time code to finish signing in."
      footer={
        <>
          No account yet?{' '}
          <Link to="/register" className="text-surge-400 hover:text-surge-300">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && <Alert tone="error">{error.message}</Alert>}

        <Field label="Email" htmlFor="email" errors={fieldErrors(error?.fields, 'email')}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <div className="mb-5 text-right">
          <Link to="/forgot-password" className="text-sm text-surge-400 hover:text-surge-300">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Checking…' : 'Continue'}
        </Button>
      </form>
    </AuthShell>
  );
}

function OtpStep({
  challenge,
  onExpired,
}: {
  challenge: LoginChallenge;
  onExpired: () => void;
}) {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [resent, setResent] = useState(false);
  const [current, setCurrent] = useState(challenge);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const session = await api.verifyOtp(current.challenge_id, code);
      signIn(session);
      navigate('/app', { replace: true });
    } catch (err) {
      const apiError =
        err instanceof ApiError ? err : new ApiError('Something went wrong.', 0);
      setError(apiError);
      setCode('');

      // An expired or exhausted challenge cannot be retried - send the user
      // back to the password step rather than leaving them typing into a
      // challenge the server has already closed.
      if (
        apiError.code === 'auth.otp_expired' ||
        apiError.code === 'auth.otp_attempts_exceeded'
      ) {
        window.setTimeout(onExpired, 2500);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    setError(null);
    try {
      setCurrent(await api.resendOtp(current.challenge_id));
      setResent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('Something went wrong.', 0));
    }
  }

  return (
    <AuthShell
      title="Enter your code"
      subtitle={`We emailed you a ${current.otp_digits}-digit code. It expires shortly and can only be used once.`}
      footer={
        <button type="button" onClick={onExpired} className="hover:text-hull-200">
          Use a different account
        </button>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && <Alert tone="error">{error.message}</Alert>}
        {resent && !error && <Alert tone="success">A new code is on its way.</Alert>}

        <Field label="One-time code" htmlFor="code">
          <Input
            id="code"
            ref={inputRef}
            // `one-time-code` lets iOS and Android offer the code straight from
            // the notification, which is the difference between this being
            // pleasant and being a chore.
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={current.otp_digits}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="text-center font-mono text-2xl tracking-[0.4em]"
          />
        </Field>

        <Button type="submit" disabled={busy || code.length < 4} className="w-full">
          {busy ? 'Verifying…' : 'Sign in'}
        </Button>

        <button
          type="button"
          onClick={handleResend}
          className="mt-4 w-full text-sm text-hull-400 hover:text-hull-200"
        >
          Send another code
        </button>
      </form>
    </AuthShell>
  );
}
